/**
 * Client-side copy of backend `app.underwriting.dealfields.derive`, kept ONLY
 * for records the server does not hold (the illustrative pipeline book).
 *
 * A deal on the server previews its edits through POST
 * /loans/{id}/derivation/preview, which runs the engine's own rule set on the
 * engine's scale. This copy had drifted from the engine's rule set (ECON-29)
 * and read the LTV column's stored fraction as a percent, which is how the
 * save bar printed a $335K "implied" loan (LPR-10). Both are fixed here too —
 * the list below is pinned to `DERIVATION_RULES` by a backend test — but
 * nothing a server-backed deal shows depends on this copy.
 */

import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import { buildPatch, parseMoneyText, readFieldValue } from '@/services/dealFile'
import { DATE_PREVIEW_FIELDS } from './derivedDates'
import { ioCoverage, rateFromPoints } from './ioDscr'

const AGREEMENT_TOLERANCE = 0.005

/** A rule over numeric inputs only. */
type PlainRule = {
  target: string
  inputs: readonly string[]
  fn: (...args: number[]) => number
  note: string
  context?: undefined
  /** A fallback basis: applies only while none of these legs is a stated figure. */
  requiresAbsent?: readonly string[]
}

/**
 * A rule that also reads raw, non-numeric values — a select such as the day
 * count, the asset class — passed to `fn` as one object beside the numeric
 * inputs (backend `Rule.context`).
 */
type ContextRule = {
  target: string
  inputs: readonly string[]
  context: readonly string[]
  fn: (args: number[], context: Record<string, unknown>) => number
  note: string
  requiresAbsent?: readonly string[]
}

type Rule = PlainRule | ContextRule

/** Fields whose stored zero means "no package stated one": a fallback rule
 *  treats such a leg as absent (backend `_ZERO_MEANS_UNSTATED`). */
const ZERO_MEANS_UNSTATED: ReadonlySet<string> = new Set(['fully_funded_amount'])

function legStated(name: string, known: Record<string, number>): boolean {
  if (!(name in known)) return false
  return !(ZERO_MEANS_UNSTATED.has(name) && known[name] <= 0)
}

/** WP-G: NOI over interest-only debt service, accrued on the deal's day count
 *  (Actual/360 when it states none). NaN — no value — wherever it is undefined. */
const ioDscrRule = (args: number[], context: Record<string, unknown>): number => {
  const [noi, principal, rate] = args
  return ioCoverage(noi, principal, rate, context.day_count)?.dscr ?? Number.NaN
}

/** A lodging asset the deal STATES it is (backend `_is_lodging_class`): an
 *  unstated class is not assumed a hotel. */
function isLodging(assetType: unknown): boolean {
  return String(assetType ?? '').trim().toLowerCase() === 'hospitality'
}

/** Hotel only (the deal states Hospitality), and only from an entered reserve. */
const hotelDscrAfterFfeRule = (args: number[], context: Record<string, unknown>): number => {
  const [noi, reserve, principal, rate] = args
  if (!isLodging(context.asset_type)) return Number.NaN
  return ioCoverage(noi, principal, rate, context.day_count, { reserve, hotelBasis: true })?.dscr ?? Number.NaN
}

/** RevPAR is ADR x occupancy for a hotel only (DER-10); occupancy is on the 0-100 scale. */
const revparRule = (args: number[], context: Record<string, unknown>): number => {
  const [adr, occupancy] = args
  return isLodging(context.asset_type) ? adr * (occupancy / 100) : Number.NaN
}

/** A number out of a raw value the way backend `_as_number` reads one ("1,200", "$5", "36%"). */
function looseNumber(value: unknown): number | null {
  if (typeof value === 'boolean' || value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const cleaned = value.trim().replace(/,/g, '').replace(/\$/g, '').replace(/%+$/, '')
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

/** An amortization type that says the loan never amortizes. "Partial IO" is an
 *  IO period followed by amortization and is not matched (backend `_INTEREST_ONLY_TYPE_RE`). */
const INTEREST_ONLY_TYPE = /\binterest[\s-]*only\b|^\s*io\b/i

/** Does the loan pay interest only for its whole initial term (backend
 *  `full_term_interest_only`)? An unstated term or IO period decides nothing. */
function fullTermInterestOnly(ioMonths: unknown, termMonths: unknown, amortizationType: unknown): boolean {
  const kind = String(amortizationType ?? '')
  if (INTEREST_ONLY_TYPE.test(kind) && !kind.toLowerCase().includes('partial')) return true
  const io = looseNumber(ioMonths)
  const term = looseNumber(termMonths)
  return io !== null && term !== null && term > 0 && io >= term
}

/** 12 x the monthly annuity payment over the amortization period (DER-01; backend
 *  `_amortizing_debt_service_rule`). `rate` is the catalogue's percentage points; outside the credible
 *  coupon band it is read as nothing. NaN (no value) for a full-term interest-only loan, a
 *  non-positive principal or an amortization under a month. */
const amortizingDebtServiceRule = (args: number[], context: Record<string, unknown>): number => {
  const [principal, ratePoints, amortizationMonths] = args
  const rate = rateFromPoints(ratePoints)
  if (rate === null || !(principal > 0) || !(amortizationMonths >= 1)) return Number.NaN
  if (fullTermInterestOnly(context.io_months, context.term_months, context.amortization_type)) return Number.NaN
  const monthly = rate / 12
  const payment = (principal * monthly) / (1 - Math.pow(1 + monthly, -amortizationMonths))
  const annual = payment * 12
  return annual > 0 ? annual : Number.NaN
}

/**
 * Forwards only, mirroring `DERIVATION_RULES`. Rule R3 (Derived-Value Conflict
 * Rule, 22 Sep 2026): a ratio a document states — cap rate, LTV, DSCR, debt
 * yield, LTC, exit cap — never back-solves an input. The value <- loan / LTV,
 * value <- NOI / cap, NOI <- value x cap, debt service <- NOI / DSCR, cost <-
 * loan / LTC and the stabilized value <-> NOI rules are retired on both sides;
 * this is the side that printed Kestrel Landing's "$88.6M" ($62M / 70%) as an
 * appraisal. `RULES` is exported so a test pins the two lists together.
 */
export const RULES: readonly Rule[] = [
  { target: 'ltv', inputs: ['loan_amount', 'property_value'], fn: (amt, v) => (amt / v) * 100, note: 'loan / value' },
  { target: 'cap_rate', inputs: ['noi', 'property_value'], fn: (noi, v) => (noi / v) * 100, note: 'NOI / value' },
  { target: 'debt_yield', inputs: ['noi', 'loan_amount'], fn: (noi, amt) => (noi / amt) * 100, note: 'NOI / loan' },
  // DER-06 / DER-07: one basis per figure — the purchase price where the deal states one, the
  // as-is value where it does not — each formula naming its basis; the value basis has fields of its own.
  { target: 'price_per_unit', inputs: ['purchase_price', 'units'], fn: (price, units) => price / units, note: 'purchase price / units' },
  { target: 'price_per_unit', inputs: ['property_value', 'units'], fn: (v, units) => v / units, note: 'value / units', requiresAbsent: ['purchase_price'] },
  { target: 'price_per_sf', inputs: ['purchase_price', 'sf'], fn: (price, sf) => price / sf, note: 'purchase price / SF' },
  { target: 'price_per_sf', inputs: ['property_value', 'sf'], fn: (v, sf) => v / sf, note: 'value / SF', requiresAbsent: ['purchase_price'] },
  { target: 'value_per_unit', inputs: ['property_value', 'units'], fn: (v, units) => v / units, note: 'value (as-is) / units' },
  { target: 'value_per_sf', inputs: ['property_value', 'sf'], fn: (v, sf) => v / sf, note: 'value (as-is) / SF' },
  { target: 'loan_to_purchase_price', inputs: ['loan_amount', 'purchase_price'], fn: (amt, price) => (amt / price) * 100, note: 'loan / purchase price' },
  { target: 'loan_per_unit', inputs: ['loan_amount', 'units'], fn: (amt, units) => amt / units, note: 'loan / units' },
  { target: 'loan_per_sf', inputs: ['loan_amount', 'sf'], fn: (amt, sf) => amt / sf, note: 'loan / SF' },
  // DER-08: LTC and equity on the total commitment where the deal records one, the loan amount
  // where it does not.
  { target: 'ltc', inputs: ['fully_funded_amount', 'total_cost'], fn: (total, cost) => (total > 0 ? (total / cost) * 100 : Number.NaN), note: 'total commitment / total cost' },
  { target: 'ltc', inputs: ['loan_amount', 'total_cost'], fn: (amt, cost) => (amt / cost) * 100, note: 'loan / total cost', requiresAbsent: ['fully_funded_amount'] },
  { target: 'fully_funded_amount', inputs: ['initial_funding', 'future_funding'], fn: (init, ff) => init + ff, note: 'initial advance + future funding' },
  { target: 'future_funding', inputs: ['fully_funded_amount', 'initial_funding'], fn: (total, init) => (total >= init ? total - init : Number.NaN), note: 'total commitment - initial advance' },
  { target: 'initial_funding', inputs: ['fully_funded_amount', 'future_funding'], fn: (total, future) => (total >= future ? total - future : Number.NaN), note: 'total commitment - future funding' },
  {
    target: 'expense_ratio',
    inputs: ['operating_expenses', 'gross_revenue'],
    fn: (opex, rev) => (opex <= rev ? (opex / rev) * 100 : Number.NaN),
    note: 'opex / revenue',
  },
  { target: 'noi', inputs: ['gross_revenue', 'operating_expenses'], fn: (rev, opex) => rev - opex, note: 'revenue - opex' },
  { target: 'operating_expenses', inputs: ['gross_revenue', 'noi'], fn: (rev, noi) => (rev >= noi ? rev - noi : Number.NaN), note: 'revenue - NOI' },
  { target: 'gross_revenue', inputs: ['noi', 'operating_expenses'], fn: (noi, opex) => noi + opex, note: 'NOI + opex' },
  { target: 'total_equity', inputs: ['total_cost', 'fully_funded_amount'], fn: (cost, total) => (total > 0 && cost >= total ? cost - total : Number.NaN), note: 'total cost - total commitment' },
  { target: 'total_equity', inputs: ['total_cost', 'loan_amount'], fn: (cost, amt) => (cost >= amt ? cost - amt : Number.NaN), note: 'total cost - loan', requiresAbsent: ['fully_funded_amount'] },
  { target: 'total_cost', inputs: ['fully_funded_amount', 'total_equity'], fn: (total, eq) => (total > 0 ? total + eq : Number.NaN), note: 'total commitment + equity' },
  { target: 'total_cost', inputs: ['loan_amount', 'total_equity'], fn: (amt, eq) => amt + eq, note: 'loan + equity', requiresAbsent: ['fully_funded_amount'] },
  // DER-10: forward rules for three catalogue fields nothing filled. RevPAR is a hotel's figure and
  // only a hotel's; breakeven occupancy is carried by the stated debt service, else the amortizing one.
  { target: 'revpar', inputs: ['adr', 'occupancy'], context: ['asset_type'], fn: revparRule, note: 'ADR x occupancy' },
  { target: 'yield_on_cost', inputs: ['noi_stabilized', 'total_cost'], fn: (noi, cost) => (noi / cost) * 100, note: 'stabilized NOI / total cost' },
  { target: 'breakeven_occupancy', inputs: ['operating_expenses', 'annual_debt_service', 'gross_potential_rent'], fn: (opex, ads, gpr) => ((opex + ads) / gpr) * 100, note: '(opex + annual debt service) / gross potential rent' },
  { target: 'breakeven_occupancy', inputs: ['operating_expenses', 'annual_debt_service_amortizing', 'gross_potential_rent'], fn: (opex, ads, gpr) => ((opex + ads) / gpr) * 100, note: '(opex + amortizing annual debt service) / gross potential rent', requiresAbsent: ['annual_debt_service'] },
  { target: 'dscr', inputs: ['noi', 'annual_debt_service'], fn: (noi, ads) => noi / ads, note: 'NOI / annual debt service' },
  { target: 'dscr_stabilized', inputs: ['noi_stabilized', 'annual_debt_service'], fn: (noi, ads) => noi / ads, note: 'stabilized NOI / annual debt service' },
  { target: 'cap_rate_stabilized', inputs: ['noi_stabilized', 'value_stabilized'], fn: (noi, value) => (noi / value) * 100, note: 'stabilized NOI / stabilized value' },
  { target: 'cash_on_cash', inputs: ['ncf', 'total_equity'], fn: (ncf, equity) => (equity > 0 ? (ncf / equity) * 100 : Number.NaN), note: 'NCF / total equity' },
  // WP-G: a stabilized measure is on the fully funded loan — the total
  // commitment where the deal records one, the loan amount where it does not.
  { target: 'ltv_stabilized', inputs: ['fully_funded_amount', 'value_stabilized'], fn: (total, v) => (total > 0 ? (total / v) * 100 : Number.NaN), note: 'total commitment / stabilized value' },
  { target: 'ltv_stabilized', inputs: ['loan_amount', 'value_stabilized'], fn: (amt, v) => (amt / v) * 100, note: 'loan / stabilized value', requiresAbsent: ['fully_funded_amount'] },
  { target: 'debt_yield_stabilized', inputs: ['noi_stabilized', 'fully_funded_amount'], fn: (noi, total) => (total > 0 ? (noi / total) * 100 : Number.NaN), note: 'stabilized NOI / total commitment' },
  { target: 'debt_yield_stabilized', inputs: ['noi_stabilized', 'loan_amount'], fn: (noi, amt) => (noi / amt) * 100, note: 'stabilized NOI / loan', requiresAbsent: ['fully_funded_amount'] },
  // WP-G: interest-only coverage, Actual/360 unless the deal states otherwise,
  // beside (never in place of) the stated DSCR. In-place on the initial advance,
  // stabilized on the total commitment; the loan amount stands in only where the
  // deal records no such leg. `rate` is read as ONE number ("6.35%"): a floating
  // quote has none, and no index level is assumed.
  {
    target: 'dscr_io',
    inputs: ['noi', 'initial_funding', 'rate'],
    context: ['day_count'],
    fn: ioDscrRule,
    note: 'NOI / interest-only debt service (initial advance x rate x day-count factor, Actual/360 = 365/360)',
  },
  {
    target: 'dscr_io',
    inputs: ['noi', 'loan_amount', 'rate'],
    context: ['day_count'],
    fn: ioDscrRule,
    note: 'NOI / interest-only debt service (loan amount x rate x day-count factor, Actual/360 = 365/360)',
    requiresAbsent: ['initial_funding'],
  },
  {
    target: 'dscr_io_stabilized',
    inputs: ['noi_stabilized', 'fully_funded_amount', 'rate'],
    context: ['day_count'],
    fn: ioDscrRule,
    note: 'stabilized NOI / interest-only debt service (total commitment x rate x day-count factor, Actual/360 = 365/360)',
  },
  {
    target: 'dscr_io_stabilized',
    inputs: ['noi_stabilized', 'loan_amount', 'rate'],
    context: ['day_count'],
    fn: ioDscrRule,
    note: 'stabilized NOI / interest-only debt service (loan amount x rate x day-count factor, Actual/360 = 365/360)',
    requiresAbsent: ['fully_funded_amount'],
  },
  {
    target: 'dscr_hotel_after_ffe',
    inputs: ['noi', 'reserve', 'initial_funding', 'rate'],
    context: ['day_count', 'asset_type'],
    fn: hotelDscrAfterFfeRule,
    note: '(NOI - FF&E reserve) / interest-only debt service (initial advance x rate x day-count factor, Actual/360 = 365/360)',
  },
  {
    target: 'dscr_hotel_after_ffe',
    inputs: ['noi', 'reserve', 'loan_amount', 'rate'],
    context: ['day_count', 'asset_type'],
    fn: hotelDscrAfterFfeRule,
    note: '(NOI - FF&E reserve) / interest-only debt service (loan amount x rate x day-count factor, Actual/360 = 365/360)',
    requiresAbsent: ['initial_funding'],
  },
  // DER-09: the same interest-only coverage on the INDICATIVE rate a floating deal gets from the
  // dated SOFR print plus its spread. The server writes that rate as a Suggestion and the browser
  // only reads it; the rate the deal states stays text, so the rules above still read nothing on a
  // floating deal.
  {
    target: 'dscr_io_indicative',
    inputs: ['noi', 'initial_funding', 'rate_indicative'],
    context: ['day_count'],
    fn: ioDscrRule,
    note: 'NOI / interest-only debt service (initial advance x indicative rate x day-count factor, Actual/360 = 365/360)',
  },
  {
    target: 'dscr_io_indicative',
    inputs: ['noi', 'loan_amount', 'rate_indicative'],
    context: ['day_count'],
    fn: ioDscrRule,
    note: 'NOI / interest-only debt service (loan amount x indicative rate x day-count factor, Actual/360 = 365/360)',
    requiresAbsent: ['initial_funding'],
  },
  // DER-01: amortizing debt service and coverage, beside (never in place of) the stated ones. A
  // floating quote reads as nothing; a full-term interest-only loan gets no amortizing figure.
  {
    target: 'annual_debt_service_amortizing',
    inputs: ['initial_funding', 'rate', 'amortization_months'],
    context: ['io_months', 'term_months', 'amortization_type'],
    fn: amortizingDebtServiceRule,
    note: '12 x PMT(initial advance, all-in rate / 12, amortization months)',
  },
  {
    target: 'annual_debt_service_amortizing',
    inputs: ['loan_amount', 'rate', 'amortization_months'],
    context: ['io_months', 'term_months', 'amortization_type'],
    fn: amortizingDebtServiceRule,
    note: '12 x PMT(loan amount, all-in rate / 12, amortization months)',
    requiresAbsent: ['initial_funding'],
  },
  { target: 'dscr_amortizing', inputs: ['noi', 'annual_debt_service_amortizing'], fn: (noi, ads) => noi / ads, note: 'NOI / amortizing annual debt service' },
]

export type DerivationOutcome = {
  derived: Record<string, number>
  notes: Record<string, string>
  conflicts: Record<string, { computed: number; stated: number; note: string }>
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'boolean' || value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const money = parseMoneyText(value)
    if (money !== null) return money
    const cleaned = value.trim().replace(/[$,%\s,]/g, '').replace(/x$/i, '')
    if (!cleaned) return null
    const parsed = Number(cleaned)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function agrees(a: number, b: number): boolean {
  const scale = Math.max(Math.abs(a), Math.abs(b), 1e-9)
  return Math.abs(a - b) / scale <= AGREEMENT_TOLERANCE
}

export function deriveCatalogue(
  values: Record<string, unknown>,
  stated: Iterable<string> = [],
): DerivationOutcome {
  const statedSet = new Set(stated)
  const known: Record<string, number> = {}
  for (const [name, raw] of Object.entries(values)) {
    const num = asNumber(raw)
    if (num !== null) known[name] = num
  }

  const derived: Record<string, number> = {}
  const notes: Record<string, string> = {}
  const conflicts: DerivationOutcome['conflicts'] = {}

  for (let pass = 0; pass < 4; pass += 1) {
    let changed = false
    for (const rule of RULES) {
      if (rule.inputs.some((input) => !(input in known))) continue
      // A fallback basis yields while its preferred leg is a stated figure.
      if (rule.requiresAbsent?.some((leg) => legStated(leg, known))) continue
      const args = rule.inputs.map((input) => known[input])
      // Additive commitment identities treat a stated zero as a real answer
      // (future funding of $0). Division still cannot run on a zero denominator.
      const allowsZero = rule.target === 'fully_funded_amount'
        || rule.target === 'future_funding'
        || rule.target === 'initial_funding'
      if (!allowsZero && args.slice(1).some((a) => a === 0)) continue
      let computed: number
      try {
        computed = rule.context
          ? rule.fn(args, Object.fromEntries(rule.context.map((name) => [name, values[name]])))
          : rule.fn(...args)
      } catch {
        continue
      }
      if (!Number.isFinite(computed)) continue

      const existing = known[rule.target]
      if (existing === undefined) {
        known[rule.target] = computed
        derived[rule.target] = computed
        notes[rule.target] = rule.note
        changed = true
        continue
      }

      if (agrees(existing, computed)) continue
      if (statedSet.has(rule.target) || rule.target in derived) {
        conflicts[rule.target] = { computed, stated: existing, note: rule.note }
      }
    }
    if (!changed) break
  }

  for (const name of Object.keys(conflicts)) {
    delete derived[name]
    delete notes[name]
  }

  return { derived, notes, conflicts }
}

/** Merge saved values with in-progress draft for derivation input. */
export function collectCatalogueValues(
  raw: RawDealFile | undefined,
  defs: DealFieldDef[],
  draft: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!raw) return out

  for (const def of defs) {
    let value = def.name in draft ? draft[def.name] : readFieldValue(raw, def)
    // A native percent column (the LTV) stores a FRACTION; the rules run on
    // 0-100. Read from the record, 0.58 is fifty-eight percent — feeding it to
    // a rule as 0.58% is what produced the 100x implied figures.
    if (!(def.name in draft) && def.native && def.kind === 'percent' && typeof value === 'number' && Math.abs(value) <= 1.5) {
      value = value * 100
    }
    if (value !== null && value !== undefined && value !== '') out[def.name] = value
  }

  for (const [name, value] of Object.entries(raw)) {
    if (name === 'custom_fields' || name === 'id') continue
    if (value !== null && value !== undefined && value !== '' && !(name in out)) out[name] = value
  }

  const custom = raw.custom_fields
  if (custom && typeof custom === 'object') {
    for (const [name, value] of Object.entries(custom as Record<string, unknown>)) {
      if (value !== null && value !== undefined && value !== '' && !(name in out)) out[name] = value
    }
  }

  for (const [name, value] of Object.entries(draft)) {
    if (value !== null && value !== undefined && value !== '') out[name] = value
  }

  return out
}

/**
 * The numeric edits a server preview is asked about, exactly as the save
 * would write them.
 *
 * Run through the save's own normaliser, so the LTV goes as the fraction its
 * column stores and the server scales it as it scales the stored one; sending
 * the typed text let the preview and the save disagree by a factor of a
 * hundred. `null` when the draft touches no numeric field (the saved record,
 * and the last save's own result, stand); `{}` when every numeric edit is
 * back at its saved value (nothing to preview).
 */
export function previewEdits(
  defs: DealFieldDef[],
  raw: RawDealFile | undefined,
  draft: Record<string, unknown>,
): Record<string, unknown> | null {
  const numeric = new Set(
    defs.filter((d) => d.kind === 'currency' || d.kind === 'percent' || d.kind === 'number').map((d) => d.name),
  )
  const dated = new Set(defs.filter((d) => d.kind === 'date' && DATE_PREVIEW_FIELDS.has(d.name)).map((d) => d.name))
  if (!Object.keys(draft).some((name) => numeric.has(name) || dated.has(name))) return null
  const { custom_fields: custom, ...native } = buildPatch(defs, raw, draft)
  const written = { ...native, ...((custom as Record<string, unknown> | undefined) ?? {}) }
  // Text in a numeric box ("abc" in Loan Amount) is not a figure to preview:
  // the box says so inline and the save refuses it (nd-062).
  return Object.fromEntries(
    Object.entries(written).filter(([name, value]) => dated.has(name) || (numeric.has(name) && typeof value !== 'string')),
  )
}
